import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMG9wZXJhdGlvbnMlMjBwcm9mZXNzaW9uYWwlMjBkb2N1bWVudGF0aW9ufGVufDB8fHx8MTc5MDYyNTQ2NHww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay, reduce }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block will-change-transform"
      initial={reduce ? false : { y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-paper pt-14 pb-20 lg:pt-20 lg:pb-28">
      <motion.div
        aria-hidden="true"
        style={{ y: glowY }}
        className="pointer-events-none absolute -right-40 top-10 h-[560px] w-[560px] rounded-full opacity-40"
      >
        <div className="h-full w-full rounded-full border border-teal/30" />
        <div className="absolute inset-10 rounded-full border border-teal/20" />
        <div className="absolute inset-20 rounded-full border border-amber/25" />
      </motion.div>

      <div className="container-tsm relative grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <motion.p
            className="eyebrow"
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            TSM Group CLE — Think Safe Mitigation
          </motion.p>

          <h1
            className="mt-6 text-5xl font-extrabold leading-[1.04] tracking-tight text-navy sm:text-6xl lg:text-[5rem] xl:text-[5.6rem]"
            data-testid="hero-headline"
          >
            <MaskedLine delay={0.2} reduce={reduce}>Know the risk.</MaskedLine>
            <MaskedLine delay={0.32} reduce={reduce}>
              <span className="text-teal">Make your next move.</span>
            </MaskedLine>
          </h1>

          <motion.p
            className="mt-7 max-w-xl text-base leading-relaxed text-ink/70 lg:text-lg"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            data-testid="hero-supporting-copy"
          >
            TSM Group CLE helps small businesses identify operational risks, strengthen compliance processes, and
            build the systems needed to operate with greater confidence.
          </motion.p>

          <motion.div
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.78 }}
          >
            <a href="/tsm4" data-testid="hero-cta-tsm4" className="btn-gold">
              Explore TSM4
              <ArrowRight className="h-4 w-4" />
            </a>
            <a href="/mitivise" data-testid="hero-cta-mitivise" className="btn-outline">
              Discover MITIVISE
              <ArrowDown className="h-4 w-4" />
            </a>
          </motion.div>

          <motion.p
            className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-navy/50"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            data-testid="hero-meta-line"
          >
            Serving businesses throughout Northeast Ohio
            <span className="mx-3 inline-block h-1.5 w-1.5 rotate-45 bg-amber align-middle" />
            TSM4 — Four Solutions. One Smarter Business.
          </motion.p>
        </div>

        <motion.div
          className="relative lg:col-span-5"
          initial={reduce ? false : { opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="absolute -left-4 -top-4 hidden h-full w-full rounded-lg border border-teal/50 lg:block" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-lg shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
            <motion.img
              src={HERO_IMAGE}
              alt="Business professional reviewing compliance documentation at a desk"
              className="h-[380px] w-full scale-110 object-cover lg:h-[520px]"
              style={reduce ? undefined : { y: imgY }}
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-navy/10" aria-hidden="true" />
          </div>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="absolute -bottom-6 left-6 rounded-md border border-navy/10 bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.1)]"
            data-testid="hero-floating-card"
          >
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-tealdeep">
              Business Risk Management
            </p>
            <p className="mt-2 text-xl font-extrabold text-navy">
              Think <span className="text-teal">Safe</span> <span className="text-amber">Mitigation</span>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
